import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import {
  isTransientCursorError,
  useSafeLiveQuery,
} from "@/indexedDB/hooks/useSafeLiveQuery";
import { useIntersectionObserver } from "@uidotdev/usehooks";
import { debounce } from "lodash";
import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import {
  getLatestMessagesWithFileByChatIdFromLocalDB,
  getMessagesWithFileCountByChatIdFromLocalDB,
} from "@/indexedDB/services/messages";

const LOADING_DELAY = 2000;

export function useFileAttachments(activeChatId: string | null) {
  const [messagesTake, setMessagesTake] = useState(MESSAGE_LIST_PAGE_SIZE);
  const [isFullFileListLoaded, setIsFullFileListLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const debouncedSetLoadingFalse = useMemo(
    () => debounce(() => setIsLoading(false), LOADING_DELAY),
    [],
  );

  const setLoadingState = useCallback(
    (isLoadingState: boolean) => {
      if (isLoadingState) {
        debouncedSetLoadingFalse.cancel();
        setIsLoading(true);
      } else {
        debouncedSetLoadingFalse();
      }
    },
    [debouncedSetLoadingFalse],
  );

  const scrollerRef = useRef<HTMLDivElement>(null);
  const filesListRef = useRef<HTMLDivElement>(null);

  const [anchorRef, topAnchorEntry] = useIntersectionObserver({
    threshold: 0,
    root: scrollerRef.current,
    rootMargin: "0px",
  });

  // Live query messages with file attachments from IndexedDB.
  // Mirrors useMediaAttachments — queries the messages table directly so all
  // synced file messages appear regardless of whether the user has scrolled
  // to them in the middle column.
  const fileMessages = useSafeLiveQuery(
    async () => {
      if (!activeChatId) {
        return [];
      }

      try {
        setLoadingState(true);
        const result = await getLatestMessagesWithFileByChatIdFromLocalDB(
          activeChatId,
          messagesTake,
        );
        setLoadingState(false);
        return result;
      } catch (err) {
        setLoadingState(false);
        // Let the known WebKit cursor error reach useSafeLiveQuery so it can
        // serve last-good + force a fresh subscription; only surface other
        // errors as a tab-level UI error.
        if (isTransientCursorError(err)) {
          throw err;
        }
        setError(err as Error);
        return [];
      }
    },
    [activeChatId, messagesTake, retryKey],
    [],
  );

  const fileMessagesCount = useSafeLiveQuery(
    () =>
      activeChatId
        ? getMessagesWithFileCountByChatIdFromLocalDB(activeChatId)
        : 0,
    [activeChatId],
    0,
  );

  const loadMoreFiles = useCallback(() => {
    if (!filesListRef.current || !activeChatId) {
      return;
    }
    if (fileMessages.length === 0) {
      return;
    }
    if (!fileMessagesCount) {
      return;
    }

    if (messagesTake >= fileMessagesCount) {
      setIsFullFileListLoaded(true);
      return;
    }

    setMessagesTake(messagesTake + MESSAGE_LIST_PAGE_SIZE);
  }, [activeChatId, fileMessages.length, fileMessagesCount, messagesTake]);

  const debouncedLoadMore = useMemo(
    () =>
      debounce(() => {
        if (topAnchorEntry?.isIntersecting) {
          loadMoreFiles();
        }
      }, 200),
    [topAnchorEntry, loadMoreFiles],
  );

  useEffect(() => {
    debouncedLoadMore();
    return () => debouncedLoadMore.cancel();
  }, [debouncedLoadMore]);

  // Increment retryKey to force useLiveQuery to re-run its query function.
  const handleRetry = useCallback(() => {
    setError(null);
    setRetryKey((k) => k + 1);
  }, []);

  useEffect(() => {
    return () => debouncedSetLoadingFalse.cancel();
  }, [debouncedSetLoadingFalse]);

  return {
    isLoading,
    error,
    fileMessages,
    isFullFileListLoaded,
    scrollerRef,
    filesListRef,
    anchorRef,
    handleRetry,
  };
}
